"use no memo";

import { useEffect, useState } from "react";
import { ProductVisibilityTypes, Status } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { ProductSchema } from "@repo/validators";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Product } from "~/providers/product-provider";
import { useCourse } from "~/hooks/use-course";
import { useGeneral } from "~/hooks/use-general";
import { usePrinter } from "~/hooks/use-printer";
import { useProduct } from "~/hooks/use-product";
import { cn, extractDirtyFields } from "~/lib/utils";
import { useProductManager } from "~/managers/use-product-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import ProductPriceForm from "../miscellaneous/price-form";
import { Button } from "../ui/button";
import { ColorPicker } from "../ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import { Switch } from "../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const createPositionsWithEmptyValues = (
  products: Product[],
  numEmpty: number,
) => {
  const initialPositions = products
    .sort((a, b) => a.position - b.position)
    .map((product) => ({
      label: `${product.name} (${product.position})`,
      value: product.position,
    }));

  const filledPositions = initialPositions.map((item) => item.value);
  const maxPosition = filledPositions[filledPositions.length - 1] ?? 0;

  // Generate empty positions between existing positions
  const emptyPositionsBetween = [];
  for (let i = 1; i <= maxPosition; i++) {
    if (!filledPositions.includes(i)) {
      emptyPositionsBetween.push({ label: `${i}`, value: i });
    }
  }

  // Generate additional empty positions after the last position
  const emptyPositionsAfter = Array.from({ length: numEmpty }, (_, i) => ({
    label: `${maxPosition + i + 1}`,
    value: maxPosition + i + 1,
  }));

  return [
    ...initialPositions,
    ...emptyPositionsBetween,
    ...emptyPositionsAfter,
  ];
};

export function ProductForm({
  product,
  onClose,
}: {
  product?: Product;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");

  const { setUnsavedChanges } = useUnsavedChanges();

  const { createProduct, updateProduct } = useProductManager();

  const { courses } = useCourse();

  const { printRoutes } = usePrinter();

  const { modifierGroups, productCategories, products } = useProduct();

  const [priceFormOpen, setPriceFormOpen] = useState(false);

  const [selectedPrice, setSelectedPrice] = useState<
    | {
        priceId: string;
        value: number;
      }
    | undefined
  >(undefined);

  const { prices, vatRates, turnoverGroups } = useGeneral();

  const form = useForm({
    schema: ProductSchema(),
    mode: "onSubmit",
    defaultValues: {
      name: product?.name ?? "",
      color: product?.color ?? undefined,
      status: product?.status ?? Status.active,
      visibility: product?.visibility ?? [],
      preparationName: product?.preparationName ?? "",
      description: product?.description ?? "",
      position: product?.position ?? 1,
      categoryId: product?.categoryId ?? "",
      vatRateId: product?.vatRateId ?? undefined,
      courseId: product?.courseId ?? undefined,
      printRouteId: product?.printRouteId ?? undefined,
      notifyPrintRouteId: product?.notifyPrintRouteId ?? undefined,
      turnoverGroupId: product?.turnoverGroupId ?? undefined,
      modifierGroupIds: product?.modifierGroupIds ?? [],
      prices:
        product?.prices.map((price) => ({
          priceId: price.priceId,
          value: price.value,
        })) ?? [],
    },
  });

  const selectedCategory = useWatch({
    control: form.control,
    name: "categoryId",
  });

  const selectedPrintRouteId = useWatch({
    control: form.control,
    name: "printRouteId",
  });

  const selectedProducts = products.filter(
    (product) => product.categoryId === selectedCategory,
  );

  const occupiedPositions = selectedProducts.map((product) => product.position);

  const positions = createPositionsWithEmptyValues(
    products.filter((product) => product.categoryId === selectedCategory),
    10,
  ).sort((a, b) => a.value - b.value);

  const firstAvailablePosition = positions.find(
    (position) => !occupiedPositions.includes(position.value),
  )?.value;

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handlePriceFormOpen = ({
    priceId,
    value,
  }: {
    priceId?: string;
    value?: number;
  }) => {
    setPriceFormOpen(true);
    if (priceId && value) {
      setSelectedPrice({ priceId, value });
    }
  };

  const handlePriceFormClose = () => {
    setPriceFormOpen(false);
    setSelectedPrice(undefined);
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    const { dirtyFields } = form.formState;
    const dirtyData = extractDirtyFields(data, dirtyFields);
    if (product) {
      const result = await updateProduct({
        id: product.id,
        categoryId: data.categoryId,
        ...dirtyData,
      });
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createProduct(data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    }
  });

  const printRouteOptions = [
    ...printRoutes.map((printRoute) => ({
      value: printRoute.id,
      label: printRoute.name,
    })),
  ];

  const visibilityOptions = [
    ...Object.values(ProductVisibilityTypes).map((value) => ({
      label: value,
      value,
    })),
  ];

  const modifierGroupOptions = [
    ...modifierGroups.map((group) => ({
      value: group.id,
      label: group.name,
    })),
  ];

  const vatRateOptions = [
    ...vatRates.map((vatRate) => ({
      value: vatRate.id,
      label: vatRate.name,
    })),
  ];

  const turnoverGroupOptions = [
    ...turnoverGroups.map((turnoverGroup) => ({
      value: turnoverGroup.id,
      label: turnoverGroup.name,
    })),
  ];

  useEffect(() => {
    const category = productCategories.find(
      (category) => category.id === selectedCategory,
    );

    if (!category) return;

    if (!product?.color) {
      if (category.color) {
        form.setValue("color", category.color);
      }
    }

    if (!product?.vatRateId) {
      const categoryVatRate = category.vatRateId;

      if (categoryVatRate) {
        form.setValue("vatRateId", categoryVatRate);
      }
    }

    if (!product?.printRouteId) {
      const categoryPrintRouteId = category.printRouteId;

      if (categoryPrintRouteId) {
        form.setValue("printRouteId", categoryPrintRouteId);
      }
    }

    if (!product?.notifyPrintRouteId) {
      const categoryNotifyPrintRouteId = category.notifyPrintRouteId;

      if (categoryNotifyPrintRouteId) {
        form.setValue("notifyPrintRouteId", categoryNotifyPrintRouteId);
      }
    }

    if (!product?.turnoverGroupId) {
      const categoryTurnoverGroupId = category.turnoverGroupId;

      if (categoryTurnoverGroupId) {
        form.setValue("turnoverGroupId", categoryTurnoverGroupId);
      }
    }
  }, [
    form,
    product?.color,
    product?.notifyPrintRouteId,
    product?.printRouteId,
    product?.turnoverGroupId,
    product?.vatRateId,
    productCategories,
    selectedCategory,
    vatRates,
  ]);

  useEffect(() => {
    if (firstAvailablePosition && !product?.position) {
      form.setValue("position", firstAvailablePosition);
    }
  }, [form, firstAvailablePosition, product]);

  // useEffect(() => {
  //   form.reset({
  //     name: product?.name ?? "",
  //     color: product?.color ?? undefined,
  //     status: product?.status ?? Status.active,
  //     visibility: product?.visibility ?? [],
  //     preparationName: product?.preparationName ?? "",
  //     description: product?.description ?? "",
  //     position: product?.position ?? 1,
  //     categoryId: product?.categoryId ?? "",
  //     vatRateId: product?.vatRateId ?? "",
  //     courseId: product?.courseId ?? undefined,
  //     printRouteId: product?.printRouteId ?? "",
  //     notifyPrintRouteId: product?.notifyPrintRouteId ?? "",
  //     modifierGroupIds: product?.modifierGroupIds ?? [],
  //     prices:
  //       product?.prices.map((price) => ({
  //         priceId: price.priceId,
  //         value: price.value,
  //       })) ?? [],
  //   });
  // }, [product, form]);

  return (
    <Form {...form}>
      <form
        id="product-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("name")}</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="categoryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.category")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select a category..."
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={productCategories
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                  defaultValue={field.value ? [field.value] : []}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.position")}</FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={!selectedCategory}
                  maxSelect={1}
                  placeholder="Select position..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={positions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Published</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={field.value === Status.active}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? Status.active : Status.inactive);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="visibility"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("visibility")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select visibility..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={visibilityOptions}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormDescription>
                Determines where the product is visible. Leave empty to show on
                all locations.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="preparationName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("preparation_name")}</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormDescription>
                This name will be shown on the preparation ticket. Leave empty
                to use the product name.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="courseId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("course")}</FormLabel>
              <MultiSelect
                placeholder="Select course..."
                maxSelect={1}
                onValueChange={(value) => {
                  field.onChange(value[0]);
                }}
                options={courses.map((course) => ({
                  value: course.id,
                  label: course.name,
                }))}
                defaultValue={field.value ? [field.value] : []}
                value={field.value ? [field.value] : []}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="prices"
          render={({ field }) => (
            <FormItem>
              <Table
                className={cn(
                  form.formState.errors.prices && "border-2 border-red-500",
                )}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Product.price_name")}</TableHead>
                    <TableHead className="text-right">
                      {t("Product.price_value")}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {field.value.map(({ priceId, value }) => {
                    const price = prices.find((p) => p.id === priceId);
                    return (
                      <ModalInput
                        key={priceId}
                        onChange={(value) => {
                          field.onChange(
                            field.value.map((price) => {
                              if (price.priceId === priceId) {
                                return {
                                  ...price,
                                  value,
                                };
                              }

                              return price;
                            }),
                          );
                        }}
                        type="decimal"
                        value={value}
                      >
                        <TableRow role="button">
                          <TableCell className="font-medium">
                            {price?.name}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatIntToCurrency(value)}
                          </TableCell>
                        </TableRow>
                      </ModalInput>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Button
                        onClick={() => {
                          handlePriceFormOpen({});
                        }}
                        size="card"
                        type="button"
                      >
                        {t("Product.add_price")}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              {/* {form.formState.errors.prices && (
                        <span className="text-center text-lg text-red-500">
                          {form.formState.errors.prices.message}
                        </span>
                      )} */}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="vatRateId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.vat_rate")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select VAT rate..."
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={vatRateOptions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="turnoverGroupId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.turnover_group")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("Product.turnover_group_placeholder")}
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={turnoverGroupOptions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="printRouteId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.print_route")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("Product.print_route_placeholder")}
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={printRouteOptions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notifyPrintRouteId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.notify_print_route")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={printRouteOptions.filter(
                    (option) => option.value !== selectedPrintRouteId,
                  )}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormDescription>
                {t("Product.notify_print_route_description")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="modifierGroupIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.modifier_groups")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select modifier groups..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={modifierGroupOptions}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
              <FormDescription>
                Leave empty to use category modifier groups
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("ProductCategory.color")}</FormLabel>
              <FormControl>
                <ColorPicker {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <Dialog modal={true} onOpenChange={setPriceFormOpen} open={priceFormOpen}>
        <DialogContent>
          <DialogHeader />
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
            <ProductPriceForm
              onClose={({ priceId, value }) => {
                handlePriceFormClose();
                form.setValue(
                  "prices",
                  [
                    ...form
                      .getValues()
                      .prices.filter((price) => price.priceId !== priceId),
                    {
                      priceId,
                      value,
                    },
                  ],
                  { shouldDirty: true, shouldValidate: true },
                );
              }}
            />
          </div>
          <DialogFooter>
            <Button type="submit" form="product-price-form" variant="success">
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
